import React, { useEffect, useRef, useState } from 'react';
import { client, consoleLogInDev } from 'shared';
import { Modal, PillButton, Row, Stack, Text } from 'library/ui';
import { IBusinessLocation, PagingResult } from 'models';
import SelectLocations from './SelectLocations';
import LocationPermissions, { PermissionsByLocation } from './LocationPermissions';
import {
    IContactDetailRow,
    buildPermissionPayload,
    canSelectAllLocations,
    copyPermissionsToLocations,
    IAllLocationsSnapshot,
    enterAllLocationsMode,
    leaveAllLocationsMode,
    fromContactDetail,
    manageableLocations,
    togglePermission,
} from './permissionsByLocation';
import { IPermission, Role } from './models';
import SelectRoles from './SelectRoles';
import { useUserContext } from 'UserContext';

interface IEditUserProps {
    name: string;
    userId?: number;
    initialRoles?: string[];
    isModalOpen: boolean;
    onClose: () => void;
    onUserUpdated: () => void;
}

export default function EditUser({ name, userId, initialRoles, isModalOpen, onClose, onUserUpdated }: IEditUserProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedLocations, setSelectedLocations] = useState<number[]>([]);
    const [allLocationsSelected, setAllLocationsSelected] = useState(false);
    const allLocationsSnapshot = useRef<IAllLocationsSnapshot | null>(null);
    const [permissionsByLocation, setPermissionsByLocation] = useState<PermissionsByLocation>({});
    const [availableLocations, setAvailableLocations] = useState<IBusinessLocation[]>([]);
    const [availablePermissions, setAvailablePermissions] = useState<IPermission[]>([]);
    const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
    const { user } = useUserContext();
    const manageAccess = user?.manage_access;
    const allowSelectAll = canSelectAllLocations(manageAccess);

    useEffect(() => {
        if (!isModalOpen || !userId) {
            return;
        }

        Promise.all([
            client(`api/business-location/?page_size=100`) as Promise<PagingResult<IBusinessLocation>>,
            client(`api/business-contact/contact-detail/?user_id=${userId}`) as Promise<IContactDetailRow[]>,
        ])
            .then(([locationsResponse, detailRows]) => {
                const locations = manageableLocations(locationsResponse.results, manageAccess);
                setAvailableLocations(locations);
                const initial = fromContactDetail(
                    detailRows,
                    locations.map((x) => x.id),
                );
                setAllLocationsSelected(initial.allLocationsSelected);
                setSelectedLocations(initial.selectedLocations);
                setPermissionsByLocation(initial.permissionsByLocation);
            })
            .catch((error) => {
                consoleLogInDev(error);
            });
        if (!availablePermissions.length) {
            client(`api/permissions/?page_size=50`)
                .then((response: PagingResult<IPermission>) => setAvailablePermissions(response.results))
                .catch((error) => {
                    consoleLogInDev(error);
                });
        }
        client(`api/role/?type=BUSINESS_CONTACT&page_size=50`)
            .then((response: PagingResult<Role>) => {
                setAvailableRoles(response.results);
                setSelectedRoles(
                    response.results.filter((x) => initialRoles?.includes(x.display_name)).map((x) => x.id),
                );
            })
            .catch((error) => {
                consoleLogInDev(error);
            });
    }, [isModalOpen, userId]);

    function onSubmitClick() {
        setErrorMessage('');
        setIsSubmitting(true);
        client(`api/business-contact/update-contact/`, {
            body: {
                user_id: userId,
                ...buildPermissionPayload(selectedLocations, allLocationsSelected, permissionsByLocation),
                roles: selectedRoles,
            },
        })
            .then(() => {
                onUserUpdated();
            })
            .catch(() => {
                setErrorMessage('An error occurred while updating the contact.');
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }

    function onPermissionToggle(locationKey: number, permissionId: number) {
        setPermissionsByLocation((prev) => togglePermission(prev, locationKey, permissionId, availablePermissions));
    }

    function onCopyPermissionsToAll(fromLocationKey: number) {
        setPermissionsByLocation((prev) => copyPermissionsToLocations(prev, fromLocationKey, selectedLocations));
    }

    function onLocationSelectAll() {
        const allIds = availableLocations.map((x) => x.id);
        if (allLocationsSelected) {
            const snapshot = allLocationsSnapshot.current;
            const restoredLocations = snapshot ? snapshot.selected.filter((id) => allIds.includes(id)) : [];
            setAllLocationsSelected(false);
            setSelectedLocations(restoredLocations.length ? restoredLocations : allIds);
            setPermissionsByLocation((prev) => leaveAllLocationsMode(prev, allIds, snapshot));
            allLocationsSnapshot.current = null;
        } else {
            const { next, snapshot } = enterAllLocationsMode(permissionsByLocation, selectedLocations);
            allLocationsSnapshot.current = snapshot;
            setAllLocationsSelected(true);
            setSelectedLocations(allIds);
            setPermissionsByLocation(next);
        }
    }

    function onRoleSelect(id: number) {
        if (selectedRoles.includes(id)) {
            setSelectedRoles(selectedRoles.filter((x) => x !== id));
        } else {
            setSelectedRoles([...selectedRoles, id]);
        }
    }

    function isSubmitDisabled() {
        return (!allLocationsSelected && !selectedLocations.length) || isSubmitting;
    }

    function closeModal() {
        setSelectedLocations([]);
        setAllLocationsSelected(false);
        allLocationsSnapshot.current = null;
        setPermissionsByLocation({});
        onClose();
    }

    if (!isModalOpen) return null;

    return (
        <Modal title={`Edit ${name}'s Account`} onClose={closeModal} width={700}>
            <Stack gap="md">
                <Text weight="semibold">Account Information</Text>
                <SelectLocations
                    availableLocations={availableLocations}
                    selectedLocations={selectedLocations}
                    allLocationsSelected={allLocationsSelected}
                    onLocationsChange={setSelectedLocations}
                    onSelectAll={onLocationSelectAll}
                    canSelectAll={allowSelectAll}
                />
                <LocationPermissions
                    availableLocations={availableLocations}
                    selectedLocations={selectedLocations}
                    allLocationsSelected={allLocationsSelected}
                    availablePermissions={availablePermissions}
                    permissionsByLocation={permissionsByLocation}
                    onToggle={onPermissionToggle}
                    onCopyToAll={onCopyPermissionsToAll}
                />
                <SelectRoles
                    selectedRoles={selectedRoles}
                    onRoleSelect={onRoleSelect}
                    availableRoles={availableRoles}
                />
                <Row justify="end" gap="sm">
                    <PillButton variant="secondary" onClick={closeModal}>
                        Cancel
                    </PillButton>
                    <PillButton disabled={isSubmitDisabled()} onClick={onSubmitClick}>
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </PillButton>
                </Row>
                {errorMessage && <Text tone="error">{errorMessage}</Text>}
            </Stack>
        </Modal>
    );
}
